<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { jobDetailModal, closeJobDetail } from '../job-detail-modal';
import { openJobFormEdit } from '../job-form-modal';
import { useJobsStore } from '../stores';
import { confirmModal, showConfirmModal, showAlertModal } from '../confirm-modal';
import { getDocumentDownloadUrl } from '../api';
import { useDocumentAvailability } from '../useDocumentAvailability';
import { getJobDocumentHistory, DOCUMENT_LABELS, documentFileKey } from '@qjob/shared';
import {
  formatEventDate,
  formatPaymentDate,
  formatCurrency,
  getJobClientName,
  getJobTypeLabel,
  getJobDisplayName,
  getJobFolderUrl,
  statusBadgeClass,
  normalizeJobStatus,
  isPersonalJob,
  isJobPaymentOverdue,
} from '../utils';
import {
  parseSlipReferences,
  getSlipPreviewUrl,
  slipDisplayName,
  isSlipPdf,
} from '../slip-upload-helpers';

const { remove } = useJobsStore();
const deleting = ref(false);

const job = computed(() => jobDetailModal.job || {});
const statusText = computed(() => normalizeJobStatus(job.value.Status));
const personal = computed(() => isPersonalJob(job.value));
const overdue = computed(() => isJobPaymentOverdue(job.value));
const jobTitle = computed(() => String(job.value['Item / Job Title'] || '').trim());
const mapLink = computed(() => {
  const l = String(job.value['Google Maps Link'] || '').trim();
  return l && l !== '-' ? l : '';
});
const folderUrl = computed(() => getJobFolderUrl(job.value));
const { refreshDocumentAvailability, isDocumentAvailable } = useDocumentAvailability();
watch(
  () => jobDetailModal.isOpen
    ? getJobDocumentHistory(job.value).map((doc) => doc.pdfUrl).join('\n')
    : '',
  (signature) => {
    void refreshDocumentAvailability(signature.split('\n').filter(Boolean));
  },
  { immediate: true },
);

const slips = computed(() =>
  parseSlipReferences(job.value['Slip Reference']).map((ref) => ({
    fileUrl: ref.fileUrl,
    previewUrl: ref.thumbnailUrl || getSlipPreviewUrl(ref.fileUrl),
    displayName: slipDisplayName(ref),
    isPdf: isSlipPdf(ref),
  })),
);

const DOC_DEFS = [
  { label: 'ใบเสนอราคา', urlField: 'Quotation PDF URL', numberField: 'Quotation Number' },
  { label: 'ใบแจ้งหนี้ (Invoice)', urlField: 'Invoice PDF URL', numberField: 'Invoice Number' },
  { label: 'ใบเสร็จรับเงิน', urlField: 'Receipt PDF URL', numberField: 'Receipt Number' },
];
const documentLinks = computed(() => {
  const j = job.value;
  const client = String(j.Client || '').trim();
  return DOC_DEFS.filter((d) => {
    const url = String(j[d.urlField] || '').trim();
    return url && isDocumentAvailable(url);
  }).map((d) => {
    const pdfUrl = String(j[d.urlField]).trim();
    const number = String(j[d.numberField] || '').trim();
    const filename = [d.label, number || client].filter(Boolean).join('-');
    return { label: d.label, pdfUrl, downloadUrl: getDocumentDownloadUrl(pdfUrl, filename) };
  });
});
const documentHistory = computed(() => getJobDocumentHistory(job.value).filter((doc) =>
  isDocumentAvailable(doc.pdfUrl)
  && !documentLinks.value.some((latest) => documentFileKey(latest.pdfUrl) === documentFileKey(doc.pdfUrl)),
).slice().reverse().map((doc) => ({ ...doc, label: DOCUMENT_LABELS[doc.type], downloadUrl: getDocumentDownloadUrl(doc.pdfUrl, doc.number || DOCUMENT_LABELS[doc.type]) })));

function editJob() {
  const current = jobDetailModal.job;
  closeJobDetail();
  if (current) openJobFormEdit(current);
}

async function deleteJob() {
  const current = jobDetailModal.job;
  if (!current || deleting.value) return;
  const label = current.Client || current.JobID || '';
  const ok = await showConfirmModal({
    title: 'ลบคิวงาน',
    message: `ลบงาน "${label}" ?`,
    confirmText: 'ลบ',
    variant: 'danger',
  });
  if (!ok) return;
  deleting.value = true;
  try {
    await remove(current);
    closeJobDetail();
  } catch (e: any) {
    await showAlertModal({ title: 'ลบไม่สำเร็จ', message: String(e?.message || e), variant: 'danger' });
  } finally {
    deleting.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  // A stacked confirm/alert dialog owns Escape while open.
  if (e.key === 'Escape' && jobDetailModal.isOpen && !confirmModal.show) {
    e.preventDefault();
    closeJobDetail();
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div v-if="jobDetailModal.isOpen" class="modal-overlay-center z-50">
    <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" @click="closeJobDetail"></div>
    <div
      class="confirm-modal-panel relative bg-white rounded-none md:rounded-2xl shadow-xl w-full h-[100dvh] md:h-auto md:max-w-lg md:max-h-[min(92vh,880px)] flex flex-col overflow-hidden"
      @click.stop
    >
      <!-- Header -->
      <div class="h-1.5 bg-gradient-to-r from-blue-500 via-sky-500 to-cyan-400 flex-shrink-0"></div>
      <div class="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-100 flex-shrink-0">
        <div class="min-w-0">
          <p class="text-[11px] font-mono text-slate-400 truncate">ID · {{ job.JobID || '-' }}</p>
          <h3 class="text-lg font-bold text-slate-800 mt-0.5">รายละเอียดงาน</h3>
          <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span :class="['inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border', statusBadgeClass(job.Status)]">
              {{ statusText }}
            </span>
            <span v-if="personal" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-violet-50 text-violet-600 border-violet-200">
              ใช้งานส่วนตัว
            </span>
          </div>
        </div>
        <button
          type="button"
          @click="closeJobDetail"
          class="flex-shrink-0 w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="ปิด"
        >
          <i class="ph ph-x text-lg"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="px-5 py-4 overflow-y-auto overscroll-contain custom-scrollbar flex-1 space-y-5">
        <!-- Client -->
        <div class="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
            <i class="ph ph-user text-lg"></i>
          </div>
          <div class="min-w-0">
            <p class="detail-label">ลูกค้า</p>
            <p class="text-base font-semibold text-slate-800 truncate">{{ getJobClientName(job) || '-' }}</p>
            <p v-if="getJobDisplayName(job)" class="text-sm text-slate-500 truncate">{{ getJobDisplayName(job) }}</p>
          </div>
        </div>

        <!-- Info grid -->
        <div class="grid grid-cols-2 gap-x-4 gap-y-4">
          <div>
            <p class="detail-label"><i class="ph ph-tag"></i> ประเภทงาน</p>
            <p class="detail-value">{{ getJobTypeLabel(job) || '-' }}</p>
          </div>
          <div>
            <p class="detail-label"><i class="ph ph-clock"></i> เวลางาน</p>
            <p class="detail-value">{{ job['Working Time'] || '-' }}</p>
          </div>
          <div>
            <p class="detail-label"><i class="ph ph-calendar-blank"></i> วันที่งาน</p>
            <p class="detail-value">{{ formatEventDate(job['Event Date']) }}</p>
          </div>
          <div>
            <p class="detail-label"><i class="ph ph-currency-circle-dollar"></i> ราคา</p>
            <p class="detail-value">฿{{ formatCurrency(job['Price per Unit']) }}</p>
          </div>
          <div>
            <p class="detail-label"><i class="ph ph-hand-coins"></i> รับจริง</p>
            <p class="detail-value">{{ job['Actual Received'] ? '฿' + formatCurrency(job['Actual Received']) : '-' }}</p>
          </div>
          <div>
            <p class="detail-label"><i class="ph ph-calendar-check"></i> วันที่ชำระ</p>
            <p class="detail-value">{{ job['Payment Date'] ? formatPaymentDate(job['Payment Date']) : 'ยังไม่ชำระ' }}</p>
          </div>
          <div>
            <p class="detail-label"><i class="ph ph-calendar-dot"></i> กำหนดชำระ</p>
            <p class="detail-value" :class="overdue ? 'text-danger font-semibold' : ''">
              {{ job['Payment Due Date'] ? formatPaymentDate(job['Payment Due Date']) : '-' }}
              <span v-if="overdue" class="text-[11px] font-normal">· เกินกำหนด</span>
            </p>
          </div>
        </div>

        <!-- Location -->
        <div v-if="job.Location || mapLink">
          <p class="detail-label"><i class="ph ph-map-pin"></i> สถานที่</p>
          <p class="detail-value">{{ job.Location || '-' }}</p>
          <a
            v-if="mapLink"
            :href="mapLink"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-1"
          >
            <i class="ph ph-arrow-square-out"></i> เปิดใน Google Maps
          </a>
        </div>

        <!-- Item / Job Title (full itemized text, if any) -->
        <div v-if="jobTitle">
          <p class="detail-label"><i class="ph ph-list-bullets"></i> รายละเอียดงาน</p>
          <p class="detail-value whitespace-pre-line">{{ jobTitle }}</p>
        </div>

        <!-- Slips -->
        <div>
          <p class="detail-label"><i class="ph ph-receipt"></i> สลิปหลักฐานการชำระ</p>
          <div v-if="slips.length" class="flex flex-wrap gap-2 mt-1.5">
            <a
              v-for="(slip, i) in slips"
              :key="i"
              :href="slip.fileUrl"
              target="_blank"
              rel="noopener"
              class="group relative w-16 h-16 rounded-lg border border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center"
              :title="slip.displayName"
            >
              <i v-if="slip.isPdf" class="ph ph-file-pdf text-2xl text-rose-400"></i>
              <img v-else-if="slip.previewUrl" :src="slip.previewUrl" :alt="slip.displayName" class="w-full h-full object-cover" />
              <i v-else class="ph ph-image text-2xl text-slate-300"></i>
            </a>
          </div>
          <p v-else class="text-sm text-slate-400 mt-1">ยังไม่มีสลิป</p>
        </div>

        <!-- Documents -->
        <div v-if="documentLinks.length || folderUrl" class="pt-4 border-t border-slate-100">
          <p class="detail-label mb-1.5"><i class="ph ph-files"></i> เอกสาร</p>
          <div class="space-y-1.5">
            <div v-for="doc in documentLinks" :key="doc.label" class="flex items-center justify-between gap-3">
              <span class="text-sm text-slate-700">{{ doc.label }}</span>
              <span class="flex items-center gap-3 text-xs flex-shrink-0">
                <a :href="doc.pdfUrl" target="_blank" rel="noopener" class="text-slate-400 hover:text-slate-800 transition-colors">เปิด</a>
                <span class="w-px h-3 bg-slate-200"></span>
                <a :href="doc.downloadUrl" target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 font-medium transition-colors">
                  <i class="ph ph-download-simple"></i> ดาวน์โหลด
                </a>
              </span>
            </div>
            <details v-if="documentHistory.length" class="text-xs text-slate-600"><summary class="cursor-pointer rounded-lg border border-slate-200 px-3 py-2">ดูประวัติเอกสาร ({{ documentHistory.length }} เวอร์ชันก่อนหน้า)</summary><div class="mt-2 max-h-60 overflow-y-auto space-y-2"><div v-for="doc in documentHistory" :key="doc.pdfUrl" class="rounded border border-slate-200 p-2"><p>{{ doc.label }} · {{ doc.number }}</p><div class="flex gap-2 mt-2"><a :href="doc.pdfUrl" target="_blank" rel="noopener" class="rounded border border-slate-300 px-2 py-1">เปิดไฟล์</a><a :href="doc.downloadUrl" target="_blank" rel="noopener" class="rounded border border-slate-200 px-2 py-1">ดาวน์โหลด</a></div></div></div></details>
            <a
                v-if="folderUrl"
              :href="folderUrl"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-800 transition-colors mt-1"
            >
              เปิดโฟลเดอร์งานทั้งหมด <i class="ph ph-arrow-up-right"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center gap-3 flex-shrink-0">
        <button
          type="button"
          @click="deleteJob"
          :disabled="deleting"
          class="flex-shrink-0 px-4 py-2.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1.5 disabled:opacity-70"
        >
          <i :class="deleting ? 'ph ph-spinner animate-spin' : 'ph ph-trash'"></i>
          {{ deleting ? 'กำลังลบ...' : 'ลบคิวงาน' }}
        </button>
        <button
          type="button"
          @click="editJob"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-blue-700 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <i class="ph ph-pencil-simple"></i> แก้ไขงานนี้
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.detail-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
}
.detail-label :deep(i),
.detail-label i {
  font-size: 13px;
}
.detail-value {
  font-size: 14px;
  color: #1e293b;
  margin-top: 2px;
}
</style>
